/**
 * NewProgramModal.jsx — "Nouveau programme" : choix entre (1) demander au coach
 * (questionnaire + email, 1 crédit/mois inclus), (2) générer via l'IA (crédit dédié,
 * ou 5 CHF au-delà) ou (3) créer/modifier soi-même.
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, MessageSquareHeart, UserCog, Loader2, Send, Sparkles, Clock3, CheckCircle2, Star, Bot, Lock, Crown, Pencil, Layers, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useClientAuth } from '../../../context/ClientAuthContext';
import { ProgramWizard } from './ProgramWizard';

const StarRating = ({ value, onChange }) => (
  <div className="flex items-center gap-1.5" data-testid="satisfaction-rating">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)} data-testid={`satisfaction-star-${n}`}>
        <Star className={`w-7 h-7 transition-colors ${n <= value ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-700'}`} />
      </button>
    ))}
  </div>
);

export const NewProgramModal = ({ open, onClose, en, onPickBuilder, onPickAI, onPickAssisted, onPickStandard, contextProgramName }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const navigate = useNavigate();

  const goToCommunaute = () => { onClose(); navigate('/checkout/communaute'); };

  const [step, setStep] = useState('choice'); // choice | request | success
  const [elig, setElig] = useState(null);
  const [carteBlanche, setCarteBlanche] = useState(false);
  const [wizardData, setWizardData] = useState(null);
  const [satisfaction, setSatisfaction] = useState(0);
  const [liked, setLiked] = useState('');
  const [disliked, setDisliked] = useState('');
  const [preferences, setPreferences] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(contextProgramName ? 'request' : 'choice');
      setCarteBlanche(false); setWizardData(null); setSatisfaction(0); setLiked(''); setDisliked(''); setPreferences('');
      authFetch('/api/tracking/program-requests/eligibility').then((r) => r.json()).then(setElig).catch(() => setElig({ can_request: false }));
    }
  }, [open, authFetch, contextProgramName]);

  if (!open) return null;

  const chooseRequest = () => setStep('request');
  const chooseBuilder = () => { onClose(); onPickBuilder(); };
  const chooseAI = () => { onClose(); onPickAI(); };
  const chooseAssisted = () => { onClose(); onPickAssisted(); };
  const chooseStandard = () => { onClose(); onPickStandard(); };

  const submitRequest = async () => {
    setSubmitting(true);
    try {
      const res = await authFetch('/api/tracking/program-requests', {
        method: 'POST',
        body: JSON.stringify({
          carte_blanche: carteBlanche,
          goal: carteBlanche ? null : wizardData?.goal,
          location: carteBlanche ? null : wizardData?.location,
          equipment: carteBlanche ? null : wizardData?.equipment,
          equipment_tags: carteBlanche ? null : wizardData?.equipmentTags,
          days_per_week: carteBlanche ? null : (wizardData?.daysPerWeek || null),
          level: carteBlanche ? null : wizardData?.level,
          duration_weeks: carteBlanche ? null : (wizardData?.durationWeeks || null),
          session_duration_minutes: carteBlanche ? null : (wizardData?.sessionDurationMinutes ? +wizardData.sessionDurationMinutes : null),
          satisfaction: satisfaction || null,
          liked: carteBlanche ? null : liked,
          disliked: carteBlanche ? null : disliked,
          preferences: carteBlanche ? null : preferences,
          related_program_name: contextProgramName || null,
        }),
      });
      if (!res.ok) throw new Error();
      setStep('success');
    } catch {
      toast.error(L('Erreur lors de l\'envoi de votre demande.', 'Error sending your request.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="new-program-modal"
      >
        <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative w-full sm:max-w-lg bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto"
        >
          <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="close-new-program-modal"><X className="w-5 h-5" /></button>

          {step === 'choice' && (
            <div className="space-y-4">
              <h3 className="text-lg font-black text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> {L('Nouveau programme', 'New program')}</h3>
              <p className="text-sm text-zinc-400">{L('Comment souhaitez-vous obtenir votre nouveau programme ?', 'How would you like to get your new program?')}</p>

              {elig?.pending_request && (
                <div className="rounded-xl bg-blue-500/10 border border-blue-500/25 p-3 flex items-start gap-2.5" data-testid="pending-request-banner">
                  <Clock3 className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-blue-200">
                    {L('Vous avez déjà une demande en cours de traitement (délai max 48h). Vous serez notifié dès que votre coach l\'aura préparée.', 'You already have a pending request (max 48h). You will be notified as soon as your coach has prepared it.')}
                  </p>
                </div>
              )}

              <button onClick={chooseRequest} className="w-full text-left rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-zinc-900 p-4 hover:border-primary/60 transition-colors" data-testid="option-request-coach">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center shrink-0"><MessageSquareHeart className="w-5 h-5 text-white" /></div>
                  <div>
                    <p className="font-bold text-white flex items-center gap-2">
                      {L('Demander à mon coach', 'Ask my coach')}
                      {elig?.locked && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded-full px-2 py-0.5"><Lock className="w-2.5 h-2.5" /> Communauté</span>}
                    </p>
                    <p className="text-xs text-zinc-400 mt-0.5">{L('Un petit questionnaire, et Laurent vous crée un programme sur-mesure (délai max 48h). 1 crédit inclus/mois.', "A quick questionnaire, and Laurent creates a custom program for you (max 48h). 1 credit included/month.")}</p>
                  </div>
                </div>
              </button>

              <button onClick={chooseStandard} className="w-full text-left rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-zinc-900 p-4 hover:border-amber-500/60 transition-colors" data-testid="option-standard-programs">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shrink-0"><Layers className="w-5 h-5 text-white" /></div>
                  <div>
                    <p className="font-bold text-white flex items-center gap-2">
                      {L('Programme Standard Body-Up', 'Body-Up Standard Program')}
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded-full px-2 py-0.5"><Zap className="w-2.5 h-2.5" /> {L('Instantané', 'Instant')}</span>
                    </p>
                    <p className="text-xs text-zinc-400 mt-0.5">{L("20 programmes prêts à l'emploi conçus par Body-Up, par niveau. Disponible tout de suite, modifiable comme vous le souhaitez.", '20 ready-made Body-Up programs, by level. Available right away, fully editable.')}</p>
                  </div>
                </div>
              </button>

              <button onClick={chooseAI} className="w-full text-left rounded-2xl border border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-zinc-900 p-4 hover:border-blue-500/60 transition-colors" data-testid="option-ai-generate">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shrink-0"><Bot className="w-5 h-5 text-white" /></div>
                  <div>
                    <p className="font-bold text-white">{L("Générer avec l'IA", 'Generate with AI')}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{L("Répondez à un questionnaire, l'IA crée votre programme instantanément. Modifiable jusqu'à validation.", 'Answer a questionnaire, AI builds your program instantly. Editable until validated.')}</p>
                  </div>
                </div>
              </button>

              <button onClick={chooseAssisted} className="w-full text-left rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-zinc-900 p-4 hover:border-emerald-500/60 transition-colors" data-testid="option-assisted-builder">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-white" /></div>
                  <div>
                    <p className="font-bold text-white">{L('Création manuelle assistée (débutant)', 'Assisted manual creation (beginner)')}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{L('Un questionnaire vous suggère un programme complet, que vous ajustez en quelques clics.', 'A questionnaire suggests a full program, which you adjust in a few clicks.')}</p>
                  </div>
                </div>
              </button>

              <button onClick={chooseBuilder} className="w-full text-left rounded-2xl border border-white/10 bg-zinc-800/50 p-4 hover:border-white/25 transition-colors" data-testid="option-build-myself">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-zinc-700 flex items-center justify-center shrink-0"><UserCog className="w-5 h-5 text-white" /></div>
                  <div>
                    <p className="font-bold text-white">{L('Création manuelle (avancé)', 'Manual creation (advanced)')}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{L("Utilisez le même éditeur complet que votre coach (exercices, séries, méthodologies, vidéos...).", 'Use the exact same full editor as your coach (exercises, sets, methodologies, videos...).')}</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {step === 'request' && (
            <div className="space-y-4">
              <h3 className="text-lg font-black text-white flex items-center gap-2"><MessageSquareHeart className="w-5 h-5 text-primary" /> {L('Demander un nouveau programme', 'Request a new program')}</h3>

              {contextProgramName && (
                <div className="rounded-xl bg-primary/10 border border-primary/25 px-3.5 py-2.5" data-testid="adjustment-context-banner">
                  <p className="text-xs text-primary font-semibold">{L('Ajustement demandé pour :', 'Adjustment requested for:')} <span className="text-white">{contextProgramName}</span></p>
                </div>
              )}

              {!elig ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              ) : elig.locked ? (
                <div className="rounded-2xl bg-gradient-to-br from-primary/15 to-zinc-900 border border-primary/30 p-5 text-center" data-testid="request-coach-upsell">
                  <Crown className="w-9 h-9 text-primary mx-auto mb-2.5" />
                  <p className="text-white font-bold">{L('Réservé à la Communauté', 'Communauté members only')}</p>
                  <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">
                    {L("Avec Suivi Pro+, vous créez votre programme vous-même (bibliothèque complète + IA à l'usage). Pour qu'un coach certifié Body-Up le crée pour vous, passez à la Communauté (49 CHF/mois).", 'With Suivi Pro+, you build your program yourself (full library + AI on demand). For a certified Body-Up coach to build it for you, upgrade to Communauté (CHF 49/month).')}
                  </p>
                  <button onClick={goToCommunaute} className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500" data-testid="upsell-communaute-btn">
                    {L('Passer à la Communauté — 49 CHF/mois', 'Upgrade to Communauté — CHF 49/month')}
                  </button>
                </div>
              ) : !elig.can_request ? (
                <div className="rounded-2xl bg-amber-500/10 border border-amber-500/25 p-4 text-center" data-testid="request-not-eligible">
                  <Clock3 className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                  <p className="text-white font-bold text-sm">
                    {elig.reason === 'pending'
                      ? L('Une demande est déjà en cours de traitement.', 'A request is already being processed.')
                      : L('Vous avez déjà utilisé votre crédit de ce mois-ci.', "You've already used this month's credit.")}
                  </p>
                  <p className="text-xs text-zinc-400 mt-1.5">{L('1 demande de programme personnalisé par mois — contactez votre coach en messagerie si besoin urgent.', '1 custom program request per month — message your coach directly if urgent.')}</p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-zinc-500" data-testid="request-quota">{L(`Crédit disponible ce mois-ci : ${elig.coach_request_remaining}`, `Credit available this month: ${elig.coach_request_remaining}`)}</p>

                  <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-zinc-800/50 p-3.5 cursor-pointer" data-testid="carte-blanche-toggle">
                    <input type="checkbox" checked={carteBlanche} onChange={(e) => { setCarteBlanche(e.target.checked); setWizardData(null); }} className="w-4 h-4 accent-primary" />
                    <div>
                      <p className="text-sm font-bold text-white">{L('Carte blanche', 'Free rein')}</p>
                      <p className="text-xs text-zinc-500">{L('Je laisse le coach décider entièrement du nouveau programme.', 'I let the coach fully decide the new program.')}</p>
                    </div>
                  </label>

                  {!carteBlanche && !wizardData && (
                    <ProgramWizard en={en} mode="coach" onCancel={() => setCarteBlanche(true)} onComplete={setWizardData} />
                  )}

                  {!carteBlanche && wizardData && (
                    <div className="space-y-3">
                      <div className="rounded-xl bg-primary/10 border border-primary/25 px-3.5 py-2.5 flex items-center justify-between gap-2" data-testid="wizard-answers-recap">
                        <p className="text-xs text-primary font-semibold">{wizardData.goal} · {wizardData.location}</p>
                        <button onClick={() => setWizardData(null)} className="inline-flex items-center gap-1 text-[11px] font-bold text-zinc-300 hover:text-white shrink-0" data-testid="edit-wizard-answers-btn">
                          <Pencil className="w-3 h-3" /> {L('Modifier', 'Edit')}
                        </button>
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 mb-1.5 block">{L('Votre satisfaction du programme actuel', 'Your satisfaction with the current program')}</label>
                        <StarRating value={satisfaction} onChange={setSatisfaction} />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 mb-1 block">{L('Exercices que vous avez aimés', 'Exercises you liked')}</label>
                        <textarea value={liked} onChange={(e) => setLiked(e.target.value)} rows={2} className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="liked-textarea" />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 mb-1 block">{L('Exercices que vous n\'avez pas aimés', "Exercises you didn't like")}</label>
                        <textarea value={disliked} onChange={(e) => setDisliked(e.target.value)} rows={2} className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="disliked-textarea" />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 mb-1 block">{L('Vos préférences pour le nouveau programme', 'Your preferences for the new program')}</label>
                        <textarea value={preferences} onChange={(e) => setPreferences(e.target.value)} rows={2} className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="preferences-textarea" />
                      </div>

                      <p className="text-xs text-zinc-500 flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5" /> {L('Délai de création : maximum 48h.', 'Creation time: max 48h.')}</p>
                      <button onClick={submitRequest} disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-60" data-testid="submit-program-request-btn">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {L('Envoyer ma demande', 'Send my request')}
                      </button>
                    </div>
                  )}

                  {carteBlanche && (
                    <div className="space-y-3">
                      <p className="text-xs text-zinc-500 flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5" /> {L('Délai de création : maximum 48h.', 'Creation time: max 48h.')}</p>
                      <button onClick={submitRequest} disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-60" data-testid="submit-program-request-btn">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {L('Envoyer ma demande', 'Send my request')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-6" data-testid="request-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
              <h3 className="text-lg font-black text-white">{L('Demande envoyée !', 'Request sent!')}</h3>
              <p className="text-sm text-zinc-400 mt-2">{L('Laurent va créer votre nouveau programme dans les 48h. Vous serez notifié dès qu\'il est prêt.', "Laurent will create your new program within 48h. You'll be notified as soon as it's ready.")}</p>
              <button onClick={onClose} className="mt-5 px-5 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500">{L('Fermer', 'Close')}</button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NewProgramModal;

